<template>
  <div class="sap-issue-pick-table">
    <el-table
      ref="sapTable"
      :data="delivery_tableData"
      row-key="__rowKey"
      border
      default-expand-all
      :tree-props="{ children: 'children' }"
      @selection-change="delivery_onSelectionChange"
      @select="delivery_onRowSelect"
      @select-all="delivery_onSelectAll"
      size="mini"
    >
      <el-table-column type="selection" width="48" :selectable="delivery_rowSelectable" />

      <el-table-column
        prop="ItemCode"
        label="Item Code"
        min-width="170"
        show-overflow-tooltip
      />

      <el-table-column
        prop="Dscription"
        label="Item Name"
        min-width="260"
        show-overflow-tooltip
      />

      <el-table-column label="Qty" width="180">
        <template slot-scope="{ row }">
          <el-input-number
            v-model="row.__issueQty"
            :min="0"
            :max="row.__maxQty"
            :step="1"
            size="mini"
            :disabled="!!row.__parent"
            @change="delivery_onIssueQtyChange(row)"
          />
          <div class="tiny muted" v-if="row.__maxQty !== null">
            Max: {{ row.__maxQty }}
            <span v-if="row.__parent">
              · BOM: x{{ delivery_toNumber(row.BOMReqQty) || 1 }}
            </span>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="Batches" min-width="240">
        <template slot-scope="{ row }">
          <div class="batch-cell">
            <span
              v-if="Array.isArray(row.components) && row.components.length > 0"
              class="muted"
              >-</span
            >

            <template v-else>
              <el-tag size="mini">
                {{ delivery_batchAllocatedTotal(row) }} / {{ row.__issueQty }}
              </el-tag>

              <el-button
                type="text"
                size="mini"
                :disabled="!delivery_canAllocate(row)"
                @click="delivery_openBatchDialog(row)"
              >
                Allocate
              </el-button>

              <div
                v-if="delivery_batchAllocatedTotal(row) > delivery_toNumber(row.__issueQty)"
                class="tiny danger"
              >
                Total by batch exceeds row qty
              </div>

              <div
                v-if="row.__anyBatches && !row.__batchOptions.length"
                class="tiny muted"
              >
                No batches for this warehouse (switch warehouse)
              </div>

              <div
                v-if="
                  row.__anyBatches &&
                  row.__batchOptions.length &&
                  delivery_toNumber(row.__available) <= 0
                "
                class="tiny danger"
              >
                No available stock in selected warehouse
              </div>

              <div v-if="row.__anyBatches && !delivery_isRowValid(row)" class="tiny muted">
                Allocate full qty to enable checkbox
              </div>
            </template>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="Warehouse" min-width="190">
        <template slot-scope="{ row }">
          <span
            v-if="Array.isArray(row.components) && row.components.length > 0"
            class="muted"
            >-</span
          >

          <el-select
            v-else
            v-model="row.__whs"
            size="mini"
            filterable
            placeholder="Select"
            @change="delivery_onWhsChange(row)"
            :disabled="row.__warehouses.length <= 1"
            style="width: 100%;"
          >
            <el-option
              v-for="w in row.__warehouses"
              :key="w.WhsCode || '__na__'"
              :label="delivery_warehouseLabel(w)"
              :value="w.WhsCode"
              :disabled="!w.WhsCode"
            />
          </el-select>
        </template>
      </el-table-column>

      <el-table-column label="Available" width="110" align="right">
        <template slot-scope="{ row }">
          <span
            v-if="Array.isArray(row.components) && row.components.length > 0"
            class="muted"
            >-</span
          >
          <span v-else :class="{ danger: row.__available < 0 }">
            {{ row.__available }}
          </span>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      title="Batch allocation"
      :visible.sync="delivery_batchDialogVisible"
      width="780px"
      @close="delivery_onBatchDialogClose"
    >
      <div v-if="delivery_activeRow">
        <div class="dialog-head">
          <div class="strong">
            {{ delivery_activeRow.ItemCode }} — {{ delivery_activeRow.Dscription }}
          </div>
          <div class="tiny muted">
            Warehouse: {{ delivery_activeRow.__whs || "N/A" }} · Qty:
            {{ delivery_activeRow.__issueQty }} · Available: {{ delivery_activeRow.__available }} ·
            Allocated: {{ delivery_batchAllocatedTotal(delivery_activeRow) }}
          </div>
        </div>

        <div class="dialog-actions">
          <el-button
            size="mini"
            @click="delivery_autoAllocateFIFO(delivery_activeRow)"
            :disabled="
              !delivery_activeRow.__batchOptions.length || delivery_toNumber(delivery_activeRow.__available) <= 0
            "
          >
            Auto allocate (FIFO)
          </el-button>

          <el-button
            size="mini"
            @click="delivery_clearBatchAlloc(delivery_activeRow)"
            :disabled="!delivery_activeRow.__batchOptions.length"
          >
            Clear
          </el-button>
        </div>

        <div v-if="!delivery_activeRow.__batchOptions.length" class="muted">
          No batches available for the selected warehouse.
        </div>

        <div
          v-else-if="delivery_toNumber(delivery_activeRow.__available) <= 0"
          class="danger tiny"
        >
          No available stock in selected warehouse.
        </div>

        <el-table
          v-else
          :data="delivery_activeRow.__batchOptions"
          border
          size="mini"
          style="width: 100%;"
        >
          <el-table-column prop="BatchNumber" label="Batch" width="170" />

          <el-table-column label="On Hand" width="120" align="right">
            <template slot-scope="{ row: b }">
              {{ delivery_toNumber(b.OnHand) }}
            </template>
          </el-table-column>

          <el-table-column prop="ExpDate" label="Exp Date" min-width="170" />

          <el-table-column label="Qty" width="190">
            <template slot-scope="{ row: b }">
              <el-input-number
                v-model="delivery_activeRow.__batchAlloc[b.BatchNumber]"
                :min="0"
                :max="delivery_toNumber(b.OnHand)"
                :step="1"
                size="mini"
                @change="
                  (val, oldVal) =>
                    delivery_onBatchQtyChange(delivery_activeRow, b, val, oldVal)
                "
              />
            </template>
          </el-table-column>
        </el-table>

        <div class="footer-metrics">
          <div
            class="tiny"
            :class="{
              danger:
                delivery_batchAllocatedTotal(delivery_activeRow) >
                delivery_toNumber(delivery_activeRow.__issueQty),
            }"
          >
            Allocated total: {{ delivery_batchAllocatedTotal(delivery_activeRow) }} /
            {{ delivery_activeRow.__issueQty }}
          </div>

          <div
            class="tiny danger"
            v-if="delivery_batchAllocatedTotal(delivery_activeRow) > delivery_toNumber(delivery_activeRow.__issueQty)"
          >
            Fix: reduce batch quantities or increase row qty.
          </div>

          <div
            class="tiny muted"
            v-if="delivery_batchAllocatedTotal(delivery_activeRow) < delivery_toNumber(delivery_activeRow.__issueQty)"
          >
            Allocate full qty to enable checkbox.
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="delivery_batchDialogVisible = false">Close</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: "SapIssuePickTable",

  props: {
    ordr_sap_data: { type: Object, required: true },
  },

  data() {
    return {
      delivery_tableData: [],
      delivery_selectedRows: [],
      delivery_batchDialogVisible: false,
      delivery_activeRow: null,
      delivery_picking: false,
    };
  },

  watch: {
    ordr_sap_data: {
      immediate: true,
      deep: false,
      handler(v) {
        const lines = v && v.DocumentLines ? v.DocumentLines : [];
        this.delivery_tableData = this.delivery_normalizeLines(lines, null);
        this.$nextTick(() => this.delivery_enforceSelectionValidity());
      },
    },
  },

  methods: {
    delivery_normalizeLines(lines, parent = null) {
      return (lines || []).map((line) => {
        const row = { ...line };

        row.__rowKey = `${row.DocEntry}-${row.LineNum}`;
        row.__parent = parent;

        row.__maxQty = this.delivery_toNumber(
          row.OpenQty != null ? row.OpenQty : row.Quantity
        );
        if (Number.isNaN(row.__maxQty)) row.__maxQty = null;

        row.__issueQty = row.__maxQty != null ? row.__maxQty : 0;

        row.__warehouses = this.delivery_buildWarehouses(row);
        row.__whs = this.delivery_defaultWarehouse(row);

        const wh = row.__warehouses.find((w) => w.WhsCode === row.__whs) || null;
        row.__available = wh ? this.delivery_toNumber(wh.Available) : 0;

        row.__batchOptions = wh && wh.BatchNumbers ? wh.BatchNumbers : [];
        row.__anyBatches = row.__warehouses.some(
          (w) => (w.BatchNumbers || []).length > 0
        );

        row.__batchAlloc = this.delivery_initBatchAllocMap(row.__batchOptions);

        if (Array.isArray(row.components) && row.components.length) {
          row.children = this.delivery_normalizeLines(row.components, row);
          this.delivery_syncChildrenQtyByBom(row);
        }

        return row;
      });
    },

    delivery_buildWarehouses(line) {
      const ws = Array.isArray(line.WarehouseStocks) ? line.WarehouseStocks : [];
      const ws2 = ws.filter((w) => w && w.WhsCode && w.WhsCode !== "WH-01");

      if (ws2.length) {
        return ws2.map((w) => ({
          WhsCode: w.WhsCode,
          WhsName: "",
          Available: this.delivery_toNumber(w.Available != null ? w.Available : w.OnHand),
          BatchNumbers: Array.isArray(w.BatchNumbers) ? w.BatchNumbers : [],
        }));
      }

      if (line.WhsCode && line.WhsCode !== "WH-01") {
        return [
          {
            WhsCode: line.WhsCode,
            WhsName: line.WhsName || "",
            Available: this.delivery_toNumber(line.Available),
            BatchNumbers: [],
          },
        ];
      }

      return [
        {
          WhsCode: "",
          WhsName: "",
          Available: 0,
          BatchNumbers: [],
        },
      ];
    },

    delivery_defaultWarehouse(line) {
      const list = Array.isArray(line.__warehouses)
        ? line.__warehouses
        : this.delivery_buildWarehouses(line);
      if (!list.length) return "";

      const target = this.delivery_toNumber(line.AvailableSelectedWhs);
      const match = list.find((w) => this.delivery_toNumber(w.Available) === target);

      return match && match.WhsCode ? match.WhsCode : list[0].WhsCode || "";
    },

    delivery_warehouseLabel(w) {
      if (!w || !w.WhsCode) return "N/A";
      const name =
        w.WhsName && w.WhsName.trim()
          ? `${w.WhsCode} — ${w.WhsName}`
          : `${w.WhsCode}`;
      return `${name} (Avail: ${this.delivery_toNumber(w.Available)})`;
    },

    delivery_canAllocate(row) {
      if (!row) return false;
      const qty = this.delivery_toNumber(row.__issueQty);
      const avail = this.delivery_toNumber(row.__available);
      const opts = row.__batchOptions || [];
      return !!row.__whs && qty > 0 && avail > 0 && opts.length > 0;
    },

    delivery_isRowValid(row) {
      if (!row) return false;

      const qty = this.delivery_toNumber(row.__issueQty);
      if (qty <= 0) return false;

      let selfOk = true;

      if (row.__anyBatches) {
        const allocOk = this.delivery_batchAllocatedTotal(row) === qty;
        const whOk = !!row.__whs;
        const availOk = this.delivery_toNumber(row.__available) > 0;
        selfOk = whOk && availOk && allocOk;
      }

      if (row.children && row.children.length) {
        return selfOk && row.children.every((ch) => this.delivery_isRowValid(ch));
      }

      return selfOk;
    },

    delivery_rowSelectable(row) {
      return this.delivery_isRowValid(row);
    },

    delivery_enforceSelectionValidity() {
      if (!this.$refs.sapTable) return;
      const sel = (this.delivery_selectedRows || []).slice();
      const invalid = sel.filter((r) => !this.delivery_isRowValid(r));
      if (!invalid.length) return;

      this.delivery_picking = true;
      invalid.forEach((r) => this.$refs.sapTable.toggleRowSelection(r, false));
      this.delivery_picking = false;
    },

    delivery_onSelectionChange(rows) {
      this.delivery_selectedRows = rows || [];
    },

    delivery_flattenRows(rows) {
      const out = [];
      const walk = (arr) => {
        (arr || []).forEach((r) => {
          out.push(r);
          if (r.children && r.children.length) walk(r.children);
        });
      };
      walk(rows);
      return out;
    },

    delivery_getDescendants(row) {
      const out = [];
      const walk = (arr) => {
        (arr || []).forEach((r) => {
          out.push(r);
          if (r.children && r.children.length) walk(r.children);
        });
      };
      walk(row.children || []);
      return out;
    },

    delivery_onRowSelect(selection, row) {
      if (this.delivery_picking) return;
      this.delivery_picking = true;

      const isSelected = (selection || []).some(
        (r) => r.__rowKey === row.__rowKey
      );

      if (row.children && row.children.length) {
        const descendants = this.delivery_getDescendants(row);

        descendants.forEach((ch) => {
          if (!isSelected) {
            this.$refs.sapTable.toggleRowSelection(ch, false);
          } else if (this.delivery_isRowValid(ch)) {
            this.$refs.sapTable.toggleRowSelection(ch, true);
          }
        });
      }

      if (!isSelected && row.__parent) {
        const parentIsSelected = (selection || []).some(
          (r) => r.__rowKey === row.__parent.__rowKey
        );
        if (parentIsSelected) {
          this.$refs.sapTable.toggleRowSelection(row.__parent, false);
        }
      }

      this.delivery_picking = false;
      this.$nextTick(() => this.delivery_enforceSelectionValidity());
    },

    delivery_onSelectAll(selection) {
      if (this.delivery_picking) return;
      this.delivery_picking = true;

      const isSelected = (selection || []).length > 0;
      const all = this.delivery_flattenRows(this.delivery_tableData);

      all.forEach((r) => {
        if (!isSelected) {
          this.$refs.sapTable.toggleRowSelection(r, false);
          return;
        }
        if (this.delivery_isRowValid(r)) {
          this.$refs.sapTable.toggleRowSelection(r, true);
        }
      });

      this.delivery_picking = false;
      this.$nextTick(() => this.delivery_enforceSelectionValidity());
    },

    delivery_onIssueQtyChange(row) {
      const issueQty = this.delivery_toNumber(row.__issueQty);
      const totalAlloc = this.delivery_batchAllocatedTotal(row);

      if (issueQty < totalAlloc) {
        this.delivery_trimAllocationsToQty(row, issueQty);
      }

      if (row.children && row.children.length) {
        this.delivery_syncChildrenQtyByBom(row);
      }

      this.$nextTick(() => this.delivery_enforceSelectionValidity());
    },

    delivery_syncChildrenQtyByBom(parentRow) {
      const parentQty = this.delivery_toNumber(parentRow.__issueQty);

      (parentRow.children || []).forEach((child) => {
        const bomQty = this.delivery_toNumber(child.BOMReqQty);
        const factor = bomQty > 0 ? bomQty : 1;

        const desired = parentQty * factor;

        const nextQty =
          child.__maxQty != null
            ? Math.min(desired, this.delivery_toNumber(child.__maxQty))
            : desired;

        child.__issueQty = nextQty;

        const totalAlloc = this.delivery_batchAllocatedTotal(child);
        if (nextQty < totalAlloc) {
          this.delivery_trimAllocationsToQty(child, nextQty);
        }

        if (child.children && child.children.length) {
          this.delivery_syncChildrenQtyByBom(child);
        }
      });
    },

    delivery_trimAllocationsToQty(row, maxQty) {
      const total = this.delivery_batchAllocatedTotal(row);
      let needReduce = total - this.delivery_toNumber(maxQty);
      if (needReduce <= 0) return;

      const batches = (row.__batchOptions || []).slice().sort((a, b) => {
        const da = new Date(a.ExpDate || a.BatchNumber || 0).getTime();
        const db = new Date(b.ExpDate || b.BatchNumber || 0).getTime();
        return db - da;
      });

      for (const b of batches) {
        const k = b.BatchNumber;
        const q = this.delivery_toNumber(row.__batchAlloc[k]);
        if (q <= 0) continue;

        const reduce = Math.min(q, needReduce);
        row.__batchAlloc[k] = q - reduce;
        needReduce -= reduce;

        if (needReduce <= 0) break;
      }
    },

    delivery_onWhsChange(row) {
      const wh = row.__warehouses.find((w) => w.WhsCode === row.__whs) || null;
      row.__available = wh ? this.delivery_toNumber(wh.Available) : 0;

      row.__batchOptions = wh && wh.BatchNumbers ? wh.BatchNumbers : [];
      row.__batchAlloc = this.delivery_initBatchAllocMap(row.__batchOptions);

      this.delivery_onIssueQtyChange(row);
      this.$nextTick(() => this.delivery_enforceSelectionValidity());
    },

    delivery_openBatchDialog(row) {
      const wh = row.__warehouses.find((w) => w.WhsCode === row.__whs) || null;
      row.__batchOptions = wh && wh.BatchNumbers ? wh.BatchNumbers : [];
      row.__batchAlloc = this.delivery_ensureBatchAllocKeys(
        row.__batchAlloc,
        row.__batchOptions
      );

      this.delivery_activeRow = row;
      this.delivery_batchDialogVisible = true;
    },

    delivery_onBatchDialogClose() {
      this.delivery_activeRow = null;
    },

    delivery_initBatchAllocMap(batchOptions) {
      const map = {};
      (batchOptions || []).forEach((b) => {
        map[b.BatchNumber] = 0;
      });
      return map;
    },

    delivery_ensureBatchAllocKeys(existing, batchOptions) {
      const map = { ...(existing || {}) };
      (batchOptions || []).forEach((b) => {
        if (map[b.BatchNumber] == null) map[b.BatchNumber] = 0;
      });

      const allowed = new Set((batchOptions || []).map((b) => b.BatchNumber));
      Object.keys(map).forEach((k) => {
        if (!allowed.has(k)) delete map[k];
      });

      return map;
    },

    delivery_batchAllocatedTotal(row) {
      const alloc = row && row.__batchAlloc ? row.__batchAlloc : {};
      return Object.values(alloc).reduce(
        (sum, v) => sum + this.delivery_toNumber(v),
        0
      );
    },

    delivery_onBatchQtyChange(row, batch, val) {
      const key = batch.BatchNumber;
      const issueQty = this.delivery_toNumber(row.__issueQty);

      let sumExcept = 0;
      Object.keys(row.__batchAlloc || {}).forEach((k) => {
        if (k === key) return;
        sumExcept += this.delivery_toNumber(row.__batchAlloc[k]);
      });

      const allowedForThisBatch = Math.max(0, issueQty - sumExcept);

      if (this.delivery_toNumber(val) > allowedForThisBatch) {
        row.__batchAlloc[key] = allowedForThisBatch;
      }

      this.$nextTick(() => this.delivery_enforceSelectionValidity());
    },

    delivery_clearBatchAlloc(row) {
      const map = {};
      (row.__batchOptions || []).forEach((b) => {
        map[b.BatchNumber] = 0;
      });
      row.__batchAlloc = map;

      this.$nextTick(() => this.delivery_enforceSelectionValidity());
    },

    delivery_autoAllocateFIFO(row) {
      const issueQty = this.delivery_toNumber(row.__issueQty);
      if (!issueQty || !row.__batchOptions.length) return;

      const opts = row.__batchOptions.slice().sort((a, b) => {
        const da = new Date(a.ExpDate || a.BatchNumber || 0).getTime();
        const db = new Date(b.ExpDate || b.BatchNumber || 0).getTime();
        return da - db;
      });

      const map = {};
      let remaining = issueQty;

      for (const b of opts) {
        const onHand = this.delivery_toNumber(b.OnHand);
        const take = Math.min(onHand, remaining);
        map[b.BatchNumber] = take;
        remaining -= take;
        if (remaining <= 0) break;
      }

      (row.__batchOptions || []).forEach((b) => {
        if (map[b.BatchNumber] == null) map[b.BatchNumber] = 0;
      });

      row.__batchAlloc = map;

      this.$nextTick(() => this.delivery_enforceSelectionValidity());
    },

    delivery_toNumber(x) {
      const n = Number.parseFloat(x);
      return Number.isFinite(n) ? n : 0;
    },

    delivery_getSelectedPayload() {
      return (this.delivery_selectedRows || []).map((r) => ({
        DocEntry: r.DocEntry,
        LineNum: r.LineNum,
        ItemCode: r.ItemCode,
        WhsCode: r.__whs,
        IssueQty: this.delivery_toNumber(r.__issueQty),
        BatchAlloc: Object.keys(r.__batchAlloc || {})
          .map((k) => ({ BatchNumber: k, Qty: this.delivery_toNumber(r.__batchAlloc[k]) }))
          .filter((x) => x.Qty > 0),
      }));
    },
  },
};
</script>

<style scoped>
.sap-issue-pick-table .tiny {
  font-size: 12px;
  line-height: 1.2;
}
.sap-issue-pick-table .muted {
  color: #909399;
}
.sap-issue-pick-table .danger {
  color: #f56c6c;
}
.sap-issue-pick-table .strong {
  font-weight: 600;
}

.batch-cell {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.dialog-head {
  margin-bottom: 10px;
}
.dialog-actions {
  margin: 10px 0;
  display: flex;
  gap: 8px;
}
.footer-metrics {
  margin-top: 10px;
}
</style>
